import { Routes, Route, Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import Shell from "@/layout/Shell";
import { useAuth } from "@/lib/auth";
import { useFetch } from "@/lib/useFetch";
import api from "@/lib/api";
import StatCard from "@/components/StatCard";
import Badge from "@/components/Badge";
import EmptyState from "@/components/EmptyState";
import { fmtDate, cn } from "@/lib/utils";
import { Calendar, BookOpen, Megaphone, Globe, Plus, ChevronRight, Users, Award, Star, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import BookHall    from "@/pages/portal/BookHall";
import MyBookings  from "@/pages/portal/MyBookings";
import HostEvent   from "@/pages/portal/HostEvent";
import CalendarView from "@/pages/portal/CalendarView";
import { motion } from "framer-motion";

const ACT_TABS = [
  { id: "organiser",   label: "Events organised", icon: Megaphone },
  { id: "participant", label: "Participated",      icon: Users },
  { id: "certs",       label: "Certificates",      icon: Award },
  { id: "feedback",    label: "Feedback given",    icon: Star },
];

const QUICK = [
  { to: "/portal/book",   icon: BookOpen,  label: "Book a hall",  desc: "Check availability & reserve",  color: "#1346E8" },
  { to: "/portal/events", icon: Megaphone, label: "Host an event", desc: "Create event page & manage regs", color: "#22B573" },
  { to: "/events",        icon: Globe,     label: "Public events", desc: "Browse upcoming BMSIT events",   color: "#8B5CF6" },
];

function Overview() {
  const { user } = useAuth();
  const nav = useNavigate();
  const bookings = useFetch(() => api.myBookings().then(r => r?.bookings || []), []);
  const activity = useFetch(() => api.myActivity(), []);
  const [actTab, setActTab] = useState("organiser");

  const list    = bookings.data || [];
  const actData = activity.data || {};
  const firstName = user?.email?.split("@")[0]?.replace(/[._]/g, " ").split(" ")[0] || "there";
  const cap = s => s ? s.charAt(0).toUpperCase() + s.slice(1) : "";

  const renderAct = () => {
    if (activity.loading) return <div className="space-y-2">{[...Array(3)].map((_,i)=><div key={i} className="h-14 rounded-xl shimmer"/>)}</div>;
    if (actTab === "organiser") {
      const items = actData.organisedBookings || actData.organiserBookings || [];
      if (!items.length) return <EmptyState icon={Megaphone} title="No events hosted yet" description="Host your first event from a confirmed hall booking." action={<Button size="sm" onClick={() => nav("/portal/book")}>Book a hall</Button>} />;
      return <div className="space-y-2 animate-fade-up">{items.slice(0,5).map(b => (
        <Link key={b.bookingID} to={`/portal/events/manage/${b.bookingID}`}
          className="flex items-center gap-3 p-3 rounded-xl hover:bg-black/3 dark:hover:bg-white/3 transition-colors group">
          <div className="w-9 h-9 rounded-xl bg-primary/8 flex items-center justify-center shrink-0">
            <Megaphone className="h-4 w-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate">{b.eventName || b.bookingID}</div>
            <div className="text-xs text-muted-foreground">{b.hallName || b.auditorium} · {fmtDate(b.date)}</div>
          </div>
          <Badge status={b.status} />
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
        </Link>
      ))}</div>;
    }
    if (actTab === "participant") {
      const items = actData.participantRegs || actData.registrations || [];
      if (!items.length) return <EmptyState icon={Users} title="No registrations yet" description="Browse public events and register to attend." action={<Link to="/events"><Button size="sm">Browse events</Button></Link>} />;
      return <div className="space-y-2 animate-fade-up">{items.slice(0,5).map(r => (
        <div key={r.regID} className="flex items-center gap-3 p-3 rounded-xl">
          <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-950/30 flex items-center justify-center shrink-0">
            <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate">{r.eventTitle || r.bookingID}</div>
            <div className="text-xs text-muted-foreground font-mono">{r.regID}</div>
          </div>
          <Badge status={r.status} />
        </div>
      ))}</div>;
    }
    if (actTab === "certs") {
      const items = actData.certificates || [];
      if (!items.length) return <EmptyState icon={Award} title="No certificates yet" description="Certificates are issued after events you attend." />;
      return <div className="space-y-2 animate-fade-up">{items.map(c => (
        <div key={c.certID} className="flex items-center gap-3 p-3 rounded-xl">
          <div className="w-9 h-9 rounded-xl bg-amber-50 dark:bg-amber-950/30 flex items-center justify-center shrink-0">
            <Award className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate">{c.eventTitle}</div>
            <div className="text-xs font-mono text-muted-foreground">{c.certID}</div>
          </div>
          {c.verifyUrl && <a href={c.verifyUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary font-medium hover:underline">Verify ↗</a>}
        </div>
      ))}</div>;
    }
    if (actTab === "feedback") {
      const items = actData.feedbackGiven || actData.feedbacks || [];
      if (!items.length) return <EmptyState icon={Star} title="No feedback given yet" />;
      return <div className="space-y-2 animate-fade-up">{items.map((fb, i) => (
        <div key={i} className="flex items-center gap-3 p-3 rounded-xl">
          <div className="w-9 h-9 rounded-xl bg-amber-50 dark:bg-amber-950/30 flex items-center justify-center shrink-0">
            <Star className="h-4 w-4 text-amber-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate">{fb.eventTitle || fb.bookingID}</div>
            <div className="text-xs text-muted-foreground">{fb.ratingOvr ? `Overall ${fb.ratingOvr}/5` : ""} · {fmtDate(fb.submittedAt)}</div>
          </div>
        </div>
      ))}</div>;
    }
    return null;
  };

  return (
    <Shell role="portal"
      title={`Good day, ${cap(firstName)} 👋`}
      subtitle="Manage your hall bookings and events"
      actions={<Button size="sm" className="gap-1.5" onClick={() => nav("/portal/book")}><Plus className="h-3.5 w-3.5"/>New booking</Button>}
    >
      {/* ── KPI stats ──────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard label="Hall bookings"  value={list.length}                                    icon={BookOpen} accent="#1346E8" delay={0} />
        <StatCard label="Confirmed"      value={list.filter(b=>b.status==="confirmed").length}  icon={Calendar} accent="#22B573" delay={0.05} />
        <StatCard label="Events hosted"  value={actData.summary?.totalHosted || 0}              icon={Megaphone} accent="#8B5CF6" delay={0.1} />
        <StatCard label="Attended"       value={actData.summary?.totalRegistered || 0}          icon={Globe} accent="#F59E0B" delay={0.15} />
      </div>

      {/* ── Quick actions ─────────────────────────────────────── */}
      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        {QUICK.map((q, i) => (
          <motion.div key={q.to}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.05 * i, ease: [0.16,1,0.3,1] }}>
            <Link to={q.to}
              className="glass rounded-2xl p-4 flex items-center gap-4 card-tap group relative overflow-hidden">
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ background: `linear-gradient(135deg, ${q.color}06, transparent)` }} />
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-110"
                style={{ background: `${q.color}12` }}>
                <q.icon className="h-5 w-5" style={{ color: q.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">{q.label}</div>
                <div className="text-xs text-muted-foreground">{q.desc}</div>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </motion.div>
        ))}
      </div>

      {/* ── Recent bookings ───────────────────────────────────── */}
      <div className="glass rounded-2xl mb-5 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border/40">
          <h3 className="font-semibold text-sm">Recent bookings</h3>
          <Link to="/portal/bookings" className="text-xs text-primary font-medium hover:underline underline-offset-2 flex items-center gap-1">
            View all <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        <div className="p-3">
          {bookings.loading ? (
            <div className="space-y-2 p-1">{[...Array(3)].map((_,i)=><div key={i} className="h-14 rounded-xl shimmer"/>)}</div>
          ) : list.length === 0 ? (
            <EmptyState icon={BookOpen} title="No bookings yet" description="Reserve your first hall to get started." />
          ) : (
            <div className="space-y-1">
              {list.slice(0,5).map(b => (
                <Link key={b.bookingID} to="/portal/bookings"
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-black/3 dark:hover:bg-white/3 transition-colors group">
                  <div className="w-9 h-9 rounded-xl bg-primary/8 flex items-center justify-center shrink-0 text-xs font-bold text-primary">
                    {fmtDate(b.date).slice(0,2)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{b.eventName || b.auditorium || b.bookingID}</div>
                    <div className="text-xs text-muted-foreground">{b.auditorium} · {fmtDate(b.date)}</div>
                  </div>
                  <Badge status={b.status} />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── My activity ───────────────────────────────────────── */}
      <div className="glass rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border/40">
          <h3 className="font-semibold text-sm mb-3">My activity</h3>
          <div className="flex gap-1 overflow-x-auto pb-0.5">
            {ACT_TABS.map(t => (
              <button key={t.id} onClick={() => setActTab(t.id)}
                className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold shrink-0 transition-all",
                  actTab === t.id ? "bg-primary text-white shadow-sm" : "text-muted-foreground hover:text-foreground hover:bg-black/5 dark:hover:bg-white/5")}>
                <t.icon className="h-3.5 w-3.5" />{t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="p-3">{renderAct()}</div>
      </div>
    </Shell>
  );
}

export default function Portal() {
  return (
    <Routes>
      <Route index           element={<Overview />} />
      <Route path="book"     element={<BookHall />} />
      <Route path="bookings" element={<MyBookings />} />
      <Route path="events/*" element={<HostEvent />} />
      <Route path="calendar" element={<CalendarView />} />
    </Routes>
  );
}
